import type { CapturedContext } from '@/api/feedback.api';

/**
 * What the app knows about where a report came from.
 *
 * Nothing here is installed globally — no fetch wrapper, no console patch — so
 * there is zero app-wide overhead. It is read on demand when someone opens the
 * report form.
 *
 * The date range and filters are the reason this file matters. "The revenue
 * total is wrong" is unreproducible without them, and asking a dentist to
 * retype the filters they had on screen is how those reports end up closed as
 * "cannot reproduce". Everything captured is shown to the user before they
 * submit.
 */

/**
 * Filter state a page wants attached to any report filed from it.
 *
 * Pages publish their state here rather than the capture reaching into
 * component internals: a page knows what its filters mean, and a label the user
 * recognises ("Branch: Maadi") is worth more to triage than a query-string
 * fragment.
 */
export interface PageReportContext {
  /** Human-readable page name, e.g. "Balance Sheet". */
  pageName?: string;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  /** Label → displayed value. Both sides are shown to the user verbatim. */
  filters?: Record<string, unknown>;
}

let currentPageContext: PageReportContext = {};

/**
 * Publish the current page's report context.
 *
 * Called from `useReportContext`. Module-level rather than React context on
 * purpose: the report form is opened from the app shell, which sits *above*
 * every page in the tree, so a provider on the page could not reach it.
 */
export function setPageReportContext(context: PageReportContext): void {
  currentPageContext = context ?? {};
}

export function clearPageReportContext(): void {
  currentPageContext = {};
}

export function getPageReportContext(): PageReportContext {
  return currentPageContext;
}

/** The build this tab is running, so a fixed bug can be told from a stale tab. */
function appVersion(): string | undefined {
  return (
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ??
    (import.meta.env.VITE_APP_VERSION as string | undefined)
  );
}

/**
 * Assemble everything captured automatically.
 *
 * `role` and `language` come from the caller because they live in React
 * context; the rest is read from the browser.
 */
export function captureContext(
  input: {
    role?: string;
    language?: string;
  } = {},
): CapturedContext {
  const page = getPageReportContext();

  return {
    pageUrl: window.location.pathname + window.location.search,
    pageName: page.pageName,
    appVersion: appVersion(),
    dateRangeFrom: page.dateRangeFrom,
    dateRangeTo: page.dateRangeTo,
    filters: page.filters,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    role: input.role,
    language: input.language,
  };
}

/**
 * Turn captured context into labelled rows for the "what we'll send" panel.
 *
 * Returns display strings only — the caller renders them, and the user sees
 * exactly what leaves their browser.
 */
export function describeContext(
  context: CapturedContext,
  labels: {
    page: string;
    dateRange: string;
    branch: string;
    version: string;
    browser: string;
    screen: string;
  },
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];

  if (context.pageName || context.pageUrl) {
    rows.push({
      label: labels.page,
      value: context.pageName ?? context.pageUrl ?? '',
    });
  }
  if (context.dateRangeFrom || context.dateRangeTo) {
    rows.push({
      label: labels.dateRange,
      value: [context.dateRangeFrom, context.dateRangeTo]
        .filter(Boolean)
        .join(' → '),
    });
  }
  for (const [key, value] of Object.entries(context.filters ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    rows.push({ label: key, value: String(value) });
  }
  if (context.appVersion) {
    rows.push({ label: labels.version, value: context.appVersion });
  }
  if (context.viewport) {
    rows.push({ label: labels.screen, value: context.viewport });
  }
  if (context.userAgent) {
    rows.push({
      label: labels.browser,
      // The full UA string is noise on screen; the readable part is enough for
      // the user to judge what is being sent. The whole string still goes.
      value: shortUserAgent(context.userAgent),
    });
  }

  return rows;
}

function shortUserAgent(ua: string): string {
  const browser =
    /Edg\/[\d.]+/.exec(ua)?.[0] ??
    /Chrome\/[\d.]+/.exec(ua)?.[0] ??
    /Firefox\/[\d.]+/.exec(ua)?.[0] ??
    /Version\/[\d.]+ Safari/.exec(ua)?.[0] ??
    ua.slice(0, 40);
  return browser.replace('/', ' ');
}
